/**
 * TanStack Query — hierarchické query keys (TKDodo factory pattern).
 *
 * Použitie:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.zakazky.all })
 *     → invaliduje aj `list`, aj `detail` zákaziek
 *   queryClient.invalidateQueries({ queryKey: queryKeys.zakazky.detail(id) })
 *     → iba jeden konkrétny detail
 */

import type { components, operations } from "@/api/types.gen";

// Užitočné aliasy pre filter-state (vstup do list endpointov)
export type ZakazkyListFilters = {
  stav?: components["schemas"]["StavZakazky"];
  /** viac stavov čiarkou — pohľad Výroba (KOLO 242) */
  stavy?: string;
  /** len zákazky prihláseného (KOLO 242) */
  moje?: boolean;
  klient_id?: string;
  kategoria_id?: string;
  bez_kategorie?: boolean;
  search?: string;
  sort?: string;
  archiv?: boolean;
  /** výber do okna Pripojiť (KOLO 208) */
  druh?: "vypocet" | "vyroba";
  page?: number;
  per_page?: number;
};

/** Filter prehľadu podľa kategórie zákazky (KOLO 172). */
export type KategoriaFilter = { kategoria_id?: string; bez_kategorie?: boolean };

/** Rozbaľovačky Cenníka (kategórie, dodávatelia, výrobcovia) podľa záložky. */
export type CennikRozbalovackaParams = {
  sekcia?: components["schemas"]["KategoriaItem"]["sekcia"];
  druh?: components["schemas"]["DruhMaterialu"];
};

/**
 * Stĺpce, podľa ktorých smie Cenník triediť (KOLO 166) — allowlist drží
 * backend (`ZoraditPole`), typ ide z OpenAPI, nech sa nerozíde.
 */
export type CennikZoraditPole = NonNullable<
  NonNullable<
    operations["list_materialy_api_cennik_materialy_get"]["parameters"]["query"]
  >["zoradit"]
>;

/**
 * Ako dlho si appka pamätá zoznam, ktorý nie je na obrazovke (KOLO 225).
 *
 * Predvolených 5 min (`App.tsx`) bolo na zoznamy málo: po dlhšej práci
 * v editore sa Zákazky aj Cenník načítavali celé odznova s prázdnou
 * kostrou. Dlhšia pamäť neznamená staré dáta — po `staleTime` sa zoznam
 * na pozadí obnoví, len kým príde, ukazuje posledné riadky.
 */
export const ZOZNAM_GC_MS = 30 * 60 * 1000;

export type CennikFilters = {
  search?: string;
  /**
   * Sekcia cenníka (záložka) — pravidlo, kam položka patrí, drží
   * backend (KOLO 164). Zastarané `druh_csv`, `kovanie_view`
   * a `kategoria_exclude_csv` server ešte prijme, ale frontend ich od
   * KOLA 166 nepoužíva — preto tu nie sú, aby sa nevrátili.
   */
  sekcia?: components["schemas"]["KategoriaItem"]["sekcia"];
  druh?: components["schemas"]["DruhMaterialu"];
  kategoria?: string;
  /** Zúženie v rámci sekcie — pracovné dosky v importe (`PRACOVNA_DOSKA,ZASTENA`). */
  kategoria_include_csv?: string;
  dodavatel?: string;
  vyrobca?: string;
  bez_struktury?: boolean;
  dekor_kod?: string;
  z_mm?: string;
  /** Trieda hrúbky dosky (18 = 18,0–18,9 mm, KOLO 188). */
  trieda_hrubky?: number;
  sirka_mm?: string;
  /** Zo `sirka_mm` pásmo ±tolerancia — import hľadá hranu kódu z exportu (KOLO 122). */
  sirka_pasmo?: boolean;
  /** Potrebná šírka dosky — hrany, ktoré ju olepia, aj širšie (KOLO 222). */
  sirka_postacuje?: string;
  /** Trieda šírky hrany — rozsah drží backend (KOLO 222). */
  sirka_trieda?: "UZKA" | "STREDNA" | "SIRSIA" | "SIROKA" | "BLOK";
  hrubka_mm?: string;
  include_daex?: boolean;
  je_aktivny?: boolean;
  /** Len vyradené (`je_aktivny` false) — pohľad s tlačidlom Obnoviť (KOLO 166). */
  len_vyradene?: boolean;
  je_oblubene?: boolean;
  je_set?: boolean;
  set_rucny?: boolean;
  /** Len položky bez kódu alebo bez kategórie (KOLO 93). */
  nedokoncene?: boolean;
  /**
   * Len nezaradené — bez kategórie alebo s predvolenou importu
   * (`_DEFAULT_*`, `DEFAULT`); pohľad na hromadné zaradenie (KOLO 166).
   */
  nezaradene?: boolean;
  /**
   * Rozsah pickera (KOLO 166) — `true` posiela každý výber položky,
   * Cenník nie. Čo picker smie ponúknuť, rozhoduje server
   * (`services/cennik_sekcie.ponukatelne`).
   */
  vyber?: boolean;
  /**
   * S `vyber`: aj riadky, ktoré inak ustúpia dvojičke preferovaného
   * dodávateľa — prepínač „Aj iní dodávatelia" v pickeroch (KOLO 166).
   */
  aj_ini_dodavatelia?: boolean;
  /** Len položky, ktoré ten istý výrobok majú aj u iného dodávateľa (KOLO 166). */
  len_duplicity?: boolean;
  /** Len položky s odhadnutou nákupnou cenou — „skontroluj cenu" (KOLO 197). */
  skontroluj_cenu?: boolean;
  /**
   * CSV zoznam UUID — vráti práve tieto položky (KOLO 120). Importné
   * okno tak dotiahne celé riadky k návrhom mapovania jedným dopytom;
   * ostatné filtre platia ďalej, takže hrúbku odfiltruje SQL.
   */
  ids_csv?: string;
  /** Triedenie podľa stĺpca (KOLO 166); bez neho poradie servera. */
  zoradit?: CennikZoraditPole;
  smer?: "asc" | "desc";
  page?: number;
  per_page?: number;
};

/**
 * Filtre faset (KOLO 166) — tie isté ako zoznam, bez stránkovania
 * a triedenia: počty v rozbaľovačkách majú sedieť s tým, čo tabuľka ukáže.
 */
export type CennikFacetyFilters = Omit<
  CennikFilters,
  "page" | "per_page" | "ids_csv" | "zoradit" | "smer"
>;

export type KlientiFilters = {
  search?: string;
  page?: number;
  per_page?: number;
};

export type AuditFilters = {
  zakazka_id?: string;
  autor?: string;
  tabulka?: string;
  action?: string;
  od?: string;
  do?: string;
  page?: number;
  per_page?: number;
};

export type SablonyFilters = {
  search?: string;
  kategoria?: string;
  typ?: string;
};

export const queryKeys = {
  auth: {
    all: ["auth"] as const,
    me: () => [...queryKeys.auth.all, "me"] as const,
  },
  zakazky: {
    all: ["zakazky"] as const,
    lists: () => [...queryKeys.zakazky.all, "list"] as const,
    list: (filters: ZakazkyListFilters) =>
      [...queryKeys.zakazky.lists(), filters] as const,
    details: () => [...queryKeys.zakazky.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.zakazky.details(), id] as const,
    // Porez pod zakazky.all — každý PATCH dielca/zostavy invaliduje
    // aj príznak aktuálnosti výsledku porezu.
    porez: (variantaId: string) =>
      [...queryKeys.zakazky.all, "porez", variantaId] as const,
    // Nárezový plán dĺžkových — počíta sa on-demand, takže tu netreba
    // aktuálnosť; pod zakazky.all, aby ho zmena dielca prepočítala.
    narez: (variantaId: string) =>
      [...queryKeys.zakazky.all, "narez", variantaId] as const,
    // Počty dlaždíc prehľadu pod `lists()` — kalkulácia, archív, zmazanie aj
    // nová zákazka invalidujú zoznam, a tým aj počty (KOLO 171; pod
    // `zakazky.all` sa obnovili až po návrate do okna)
    stavPocty: (filtre: KategoriaFilter = {}) =>
      [...queryKeys.zakazky.lists(), "stav-pocty", filtre] as const,
    // Pás Čaká na teba (KOLO 242) — pod `lists()`, aby ho obnovila každá
    // zmena zákazky ako zoznam
    cakaNaTeba: () => [...queryKeys.zakazky.lists(), "caka-na-teba"] as const,
    // Kategórie s počtami tiež pod `lists()` — presun zákazky (PATCH),
    // archív aj zmazanie ich obnovia bez ďalšieho kódu (KOLO 172)
    kategorie: (archiv: boolean, stav: string | null = null) =>
      [...queryKeys.zakazky.lists(), "kategorie", archiv, stav] as const,
    // História cien — pribúda len pri prechode stavu, ale ten ide cez
    // PATCH zákazky, takže pod zakazky.all sa invaliduje spolu s detailom
    cenaHistoria: (zakazkaId: string) =>
      [...queryKeys.zakazky.all, "cena-historia", zakazkaId] as const,
  },
  varianty: {
    all: ["varianty"] as const,
    edgesSummaries: () =>
      [...queryKeys.varianty.all, "edges-summary"] as const,
    edgesSummary: (variantaId: string, zostavaId?: string) =>
      [...queryKeys.varianty.edgesSummaries(), variantaId, zostavaId ?? null] as const,
    kovaniaSummaries: () =>
      [...queryKeys.varianty.all, "kovania-summary"] as const,
    kovaniaSummary: (variantaId: string, zostavaId?: string) =>
      [...queryKeys.varianty.kovaniaSummaries(), variantaId, zostavaId ?? null] as const,
    // Náhľad ceny — hypotéza je súčasťou kľúča, takže TanStack sám zahodí
    // odpovede k starším vstupom a rovnaké hypotézy dedupne (KOLO 65).
    cenaNahlady: () => [...queryKeys.varianty.all, "cena-nahlad"] as const,
    cenaNahlad: (variantaId: string, hypoteza: unknown) =>
      [...queryKeys.varianty.cenaNahlady(), variantaId, hypoteza] as const,
  },
  cennik: {
    all: ["cennik"] as const,
    materialy: (filters: CennikFilters) =>
      [...queryKeys.cennik.all, "materialy", filters] as const,
    materialyStrankovane: (filters: CennikFilters) =>
      [...queryKeys.cennik.all, "materialy-strankovane", filters] as const,
    facety: (filters: CennikFacetyFilters) =>
      [...queryKeys.cennik.all, "facety", filters] as const,
    material: (id: string) =>
      [...queryKeys.cennik.all, "material", id] as const,
    // Bez parametrov `{}` — ako prefix pokryje aj kľúče s parametrami
    kategorie: (params: CennikRozbalovackaParams = {}) =>
      [...queryKeys.cennik.all, "kategorie", params] as const,
    marzeKategorii: () =>
      [...queryKeys.cennik.all, "marze-kategorii"] as const,
    // Pod cennik.all — úprava, zrušenie aj import menia percentá a počty
    upravaCienSkupiny: () =>
      [...queryKeys.cennik.all, "uprava-cien-skupiny"] as const,
    // Pod cennik.all — zmena preferencie mení zoznamy pickerov aj štítky
    preferovaniDodavatelia: () =>
      [...queryKeys.cennik.all, "preferovani-dodavatelia"] as const,
    kovaniePodlaKodu: (kod: string) =>
      [...queryKeys.cennik.all, "kovanie-podla-kodu", kod] as const,
    struktury: () => [...queryKeys.cennik.all, "struktury"] as const,
    typyDosiek: () => [...queryKeys.cennik.all, "typy-dosiek"] as const,
    navrhMaterialu: (params: Record<string, string | undefined>) =>
      [...queryKeys.cennik.all, "navrh-materialu", params] as const,
    marzeKategorie: (druh: string, kategoria: string, smer = "") =>
      [...queryKeys.cennik.all, "marze-kategorie", druh, kategoria, smer] as const,
    dodavatelia: (params: CennikRozbalovackaParams = {}) =>
      [...queryKeys.cennik.all, "dodavatelia", params] as const,
    vyrobcovia: (params: CennikRozbalovackaParams = {}) =>
      [...queryKeys.cennik.all, "vyrobcovia", params] as const,
    hrubkyDosiek: (druh?: string, includeDaex?: boolean) =>
      [...queryKeys.cennik.all, "hrubky-dosiek", druh ?? "", includeDaex ?? false] as const,
    edgesFor: (materialId: string) =>
      [...queryKeys.cennik.all, "edges-for", materialId] as const,
    edgeCandidates: (materialId: string, hrubaHranaMm?: string, slotId?: string) =>
      [...queryKeys.cennik.all, "edge-candidates", materialId, hrubaHranaMm ?? "", slotId ?? ""] as const,
  },
  slotEdges: {
    all: ["slot-edges"] as const,
    byThickness: (slotId: string, extraHrubky?: string) =>
      [...queryKeys.slotEdges.all, "by-thickness", slotId, extraHrubky ?? ""] as const,
    overrides: (slotId: string) =>
      [...queryKeys.slotEdges.all, "overrides", slotId] as const,
  },
  klienti: {
    all: ["klienti"] as const,
    list: (filters: KlientiFilters) =>
      [...queryKeys.klienti.all, "list", filters] as const,
    detail: (id: string) => [...queryKeys.klienti.all, "detail", id] as const,
  },
  operacie: {
    all: ["operacie"] as const,
    list: () => [...queryKeys.operacie.all, "list"] as const,
  },
  nazvyDielcov: {
    all: ["nazvy-dielcov"] as const,
    list: () => [...queryKeys.nazvyDielcov.all, "list"] as const,
  },
  splitRules: {
    all: ["split-rules"] as const,
    list: () => [...queryKeys.splitRules.all, "list"] as const,
  },
  sablony: {
    all: ["sablony"] as const,
    list: (filters: SablonyFilters) =>
      [...queryKeys.sablony.all, "list", filters] as const,
    detail: (id: string) =>
      [...queryKeys.sablony.all, "detail", id] as const,
  },
  komponenty: {
    all: ["komponenty"] as const,
    list: () => [...queryKeys.komponenty.all, "list"] as const,
  },
  audit: {
    all: ["audit"] as const,
    list: (filters: AuditFilters) =>
      [...queryKeys.audit.all, "list", filters] as const,
    filtre: () => [...queryKeys.audit.all, "filtre"] as const,
  },
  users: {
    all: ["users"] as const,
    list: () => [...queryKeys.users.all, "list"] as const,
  },
  praca: {
    all: ["praca"] as const,
    zakazka: (zakazkaId: string) =>
      [...queryKeys.praca.all, "zakazka", zakazkaId] as const,
    prehlad: () => [...queryKeys.praca.all, "prehlad"] as const,
  },
  objednavky: {
    all: ["objednavky"] as const,
    prehlad: (stav: string) =>
      [...queryKeys.objednavky.all, "prehlad", stav] as const,
    /** `vyber` = CSV vybraných variant (KOLO 206); bez neho predvolené servera. */
    zakazka: (zakazkaId: string, vyber = "") =>
      [...queryKeys.objednavky.all, "zakazka", zakazkaId, vyber] as const,
  },
  topsolidMapovanie: {
    all: ["topsolid-mapovanie"] as const,
    list: (druh: string, q: string) =>
      [...queryKeys.topsolidMapovanie.all, "list", druh, q] as const,
  },
  topsolidKoncepty: {
    all: ["topsolid-koncepty"] as const,
  },
  /** Osobný kľúč pre Tandem Zostavy (KOLO 128). */
  cutrite: {
    all: ["cutrite"] as const,
    /** `vyber` — id variant zoznamu čiarkou (KOLO 207, `kluceVyberu`). */
    kontrola: (zakazkaId: string, vyber: string) =>
      ["cutrite", "kontrola", zakazkaId, vyber] as const,
    /** Posledné odoslanie zoznamu — stav importu a píly (KOLO 145, 207). */
    posledne: (zakazkaId: string, vyber: string) =>
      ["cutrite", "posledne", zakazkaId, vyber] as const,
    /** Všetky zoznamy zákazky v Cut Rite (KOLO 207) — pás, hlásenia, okno. */
    linky: (zakazkaId: string) => ["cutrite", "linky", zakazkaId] as const,
  },
  mojKluc: {
    all: ["moj-kluc"] as const,
  },
  topsolidOpravy: {
    all: ["topsolid-opravy"] as const,
    zakazka: (id: string) =>
      [...queryKeys.topsolidOpravy.all, id] as const,
  },
  zalohy: {
    all: ["zalohy"] as const,
    stav: () => [...queryKeys.zalohy.all, "stav"] as const,
    /** Súbory záloh na serveri (KOLO 198). */
    subory: () => [...queryKeys.zalohy.all, "subory"] as const,
    /** Príprava zálohy do dočasnej DB — pollinguje sa, kým beží. */
    obnova: () => [...queryKeys.zalohy.all, "obnova"] as const,
    /** Zákazky v pripravenej zálohe — `pripravaId` oddelí dve zálohy. */
    zakazky: (pripravaId: number, hladat: string) =>
      [...queryKeys.zalohy.obnova(), pripravaId, "zakazky", hladat] as const,
    nahlad: (pripravaId: number, zakazkaId: string) =>
      [...queryKeys.zalohy.obnova(), pripravaId, "nahlad", zakazkaId] as const,
  },
} as const;
